/**
 * The Loom — client-side graph types + fetch.
 *
 * Mirrors the server shape in `src/lib/loom/graph-types.ts`. The data comes
 * from the read-only, super-admin-gated `GET /api/v1/loom/graph` endpoint —
 * runtime-internal agent data only, no tenant/product data.
 */
import { apiFetch } from './api'

export type LoomNodeType = 'agent' | 'tool' | 'namespace' | 'learning'
export type LoomLinkKind = 'owns' | 'reads' | 'writes' | 'delegates' | 'learned'

export interface LoomAgentNode {
  id: string
  type: 'agent'
  label: string
  skillName: string
  displayName: string
  description: string
  version: string
  model: string
  file: string
  enabled: boolean
  agentClass: 'council' | 'fixture'
  loop:
    | { onRoster: true; cadence: string; staleAfterDays: number | null; specFolder: string }
    | { onRoster: false }
  decision: {
    maxAutonomousActions: number
    requiresApprovalFor: string[]
    safeDefault: string
  }
  triggers: { cron: { expression: string; description: string }[]; events: string[] }
  born: string | null
  real: true
}

export interface LoomToolNode {
  id: string
  type: 'tool'
  label: string
  toolName: string
  owner: string
  description: string
  born: string | null
  bornApprox: boolean
  real: true
}

export interface LoomNamespaceNode {
  id: string
  type: 'namespace'
  label: string
  namespace: string
  readers: string[]
  writers: string[]
  wildcard: boolean
  shared: boolean
  born: string | null
  real: true
}

export interface LoomLearningNode {
  id: string
  type: 'learning'
  label: string
  agent: string
  week: string
  proposalType: 'threshold_tweak' | 'spec_wording_fix' | 'new_check'
  pattern: string
  proposal: string
  born: string | null
  real: false
}

export type LoomNode = LoomAgentNode | LoomToolNode | LoomNamespaceNode | LoomLearningNode

export interface LoomLink {
  source: string
  target: string
  kind: LoomLinkKind
  real: boolean
  via?: string
  born?: string | null
}

export interface LoomGraph {
  meta: {
    commit: string
    generated: string
    agentCount: number
    sources: { agents: string; roster: string; dates: string; learning: string }
  }
  nodes: LoomNode[]
  links: LoomLink[]
}

export const loomApi = {
  graph: () => apiFetch<LoomGraph>('/api/v1/loom/graph'),
}
